import cls from 'classnames';
import { clamp } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { withRCEHelpers, RCEHelpersPropTypes } from '../rce-helpers-context/index.js';
import { s as styleInject } from '../../style-inject.es-746bb8ed.js';
import { LoaderIcon } from '../../assets/icons/LoaderIcon.js';

var css_248z = ".lnGN1{align-items:center;display:flex;position:relative}._402CO,.im3d1{box-sizing:border-box;color:inherit;font-family:inherit;font-size:14px;height:auto;line-height:1;overflow:auto;z-index:2}[dir] ._402CO,[dir] .im3d1{margin:0;padding:0}._402CO{display:block;position:relative;resize:none;transition:font-size .3s;width:100%;z-index:1}[dir] ._402CO{background:transparent;border:0}._402CO::placeholder{color:inherit;opacity:.6}._402CO:focus{outline:0}[dir] ._402CO[disabled]{cursor:not-allowed}.W97Qx{font-size:18px;line-height:1.38}.kH3AG{font-size:24px;line-height:1.3}._3QQ-J{font-size:36px;line-height:1.2}.ugD-8{font-weight:700}.ugD-8.W97Qx{font-size:14px}.ugD-8.kH3AG{font-size:18px}.ugD-8._3QQ-J{font-size:22px}@supports not (contain:inline-size){@media only screen and (max-width:640px){._402CO.W97Qx,.im3d1.W97Qx{font-size:14px}._402CO.kH3AG,.im3d1.kH3AG{font-size:18px}._402CO._3QQ-J,.im3d1._3QQ-J{font-size:22px}}}@container (width < 640px){._402CO.W97Qx,.im3d1.W97Qx{font-size:14px}._402CO.kH3AG,.im3d1.kH3AG{font-size:18px}._402CO._3QQ-J,.im3d1._3QQ-J{font-size:22px}}.EeL5B.VTnCh,.EeL5B:focus,.EeL5B:focus-within,.lnGN1:hover .EeL5B{opacity:1}.EeL5B{align-items:center;display:flex;flex-direction:row;opacity:0;position:absolute;transition:opacity .5s;z-index:1}[dir] .EeL5B{border:none}[dir=ltr] .EeL5B{left:auto;right:12px}[dir=rtl] .EeL5B{left:12px;right:auto}.SswxN{position:relative;word-break:break-word;z-index:1}.K62LG{animation-duration:1.5s;animation-iteration-count:infinite;animation-name:jU9JB;animation-timing-function:linear}@keyframes jU9JB{0%{transform:rotate(0deg)}to{transform:rotate(1turn)}}";
var styles = {"root":"lnGN1","text":"im3d1","input":"_402CO","small":"W97Qx","medium":"kH3AG","large":"_3QQ-J","webview":"ugD-8","endAdornment":"EeL5B","shown":"VTnCh","value":"SswxN","spinner":"K62LG","spin":"jU9JB"};
styleInject(css_248z);

var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
class TextFieldComponent extends React.PureComponent {
  constructor() {
    super(...arguments);
    __publicField(this, "$el", React.createRef());
    __publicField(this, "state", {
      placeholder: this.props.placeholder,
      rows: 1,
      syncing: false,
      isFocused: false
    });
    __publicField(this, "handleWindowResizeEvent", () => setTimeout(() => this.resize()));
    __publicField(this, "handleFocus", (e) => {
      e.stopPropagation();
      setTimeout(() => this.resize());
      this.hidePlaceholder();
      this.props.rce.setInPluginEditingMode(true);
      this.setState({ isFocused: true });
    });
    __publicField(this, "handleBlur", () => {
      setTimeout(() => this.resize());
      this.showPlaceholder();
      this.props.rce.setInPluginEditingMode(false);
      this.setState({ isFocused: false });
    });
    __publicField(this, "handleChange", (event) => {
      this.props.onChange(event.target.value);
      this.resize();
    });
  }
  componentDidMount() {
    const { rce } = this.props;
    const isEditMode = !rce.isViewMode;
    if (isEditMode) {
      this.resize();
    }
    this.handleAutoFocus();
    window == null ? void 0 : window.addEventListener("resize", this.handleWindowResizeEvent);
  }
  componentDidUpdate(props) {
    if (this.props.rce.isViewMode === props.rce.isViewMode) {
      return;
    }
    this.handleAutoFocus();
  }
  handleAutoFocus() {
    var _a;
    const { rce, autofocus } = this.props;
    const isEditMode = !rce.isViewMode;
    if (autofocus && isEditMode) {
      (_a = this.$el.current) == null ? void 0 : _a.focus();
    }
  }
  componentWillUnmount() {
    window == null ? void 0 : window.removeEventListener("resize", this.handleWindowResizeEvent);
  }
  hidePlaceholder() {
    this.setState({ placeholder: "" });
  }
  showPlaceholder() {
    this.setState({ placeholder: this.props.placeholder });
  }
  handleKeyDown(event) {
    if (event.keyCode === 13) {
      event.preventDefault();
    }
  }
  resize() {
    if (!this.$el.current) {
      return;
    }
    const computedStyles = getComputedStyle(this.$el.current);
    const lineHeight = parseInt(computedStyles.lineHeight);
    const paddingTop = parseInt(computedStyles.paddingTop);
    const paddingBottom = parseInt(computedStyles.paddingBottom);
    this.$el.current.rows = 1;
    const { scrollHeight } = this.$el.current;
    this.$el.current.rows = this.state.rows;
    const contentHeight = scrollHeight - (paddingTop + paddingBottom);
    const rows = Math.ceil(clamp(contentHeight / lineHeight, 1, Infinity));
    this.setState({ rows });
  }
  render() {
    const {
      dataHook,
      textAutoResize,
      className,
      rce,
      endAdornment,
      style,
      children,
      startAdornment,
      maxLength,
      disabled,
      value
    } = this.props;
    const { placeholder, rows, syncing, isFocused } = this.state;
    if (rce.isViewMode || rce.isPreview) {
      return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
        "p",
        {
          style,
          className: cls(
            styles.text,
            className,
            textAutoResize && {
              [styles.small]: value.length > 120,
              [styles.medium]: value.length <= 120,
              [styles.large]: value.length <= 80
            },
            {
              [styles.webview]: rce.isWebView
            }
          )
        },
        startAdornment,
        children,
        /* @__PURE__ */ React.createElement("span", { className: styles.value, "data-hook": dataHook }, this.props.value || this.props.placeholder)
      ));
    }
    return /* @__PURE__ */ React.createElement("div", { className: styles.root }, startAdornment, /* @__PURE__ */ React.createElement(
      "textarea",
      {
        "data-hook": dataHook,
        disabled,
        maxLength,
        style,
        ref: this.$el,
        onChange: this.handleChange,
        onFocus: this.handleFocus,
        onBlur: this.handleBlur,
        placeholder,
        value,
        rows,
        onKeyDown: this.handleKeyDown,
        className: cls(
          styles.input,
          className,
          textAutoResize && {
            [styles.small]: value.length > 120,
            [styles.medium]: value.length <= 120,
            [styles.large]: value.length <= 80
          }
        ),
        onClick: void 0
      }
    ), /* @__PURE__ */ React.createElement(
      "span",
      {
        className: cls(styles.endAdornment, {
          [styles.shown]: syncing || isFocused
        })
      },
      syncing ? /* @__PURE__ */ React.createElement(LoaderIcon, { className: styles.spinner }) : endAdornment
    ));
  }
}
const TextField = withRCEHelpers(TextFieldComponent);
TextFieldComponent.defaultProps = {
  maxLength: 500,
  disabled: false
};
TextFieldComponent.propTypes = __spreadValues({
  dataHook: PropTypes.string,
  autofocus: PropTypes.bool,
  maxLength: PropTypes.number,
  value: PropTypes.string,
  disabled: PropTypes.string,
  className: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string.isRequired,
  textAutoResize: PropTypes.bool,
  startAdornment: PropTypes.node,
  endAdornment: PropTypes.node
}, RCEHelpersPropTypes);

export { TextField };
//# sourceMappingURL=TextField.js.map
